import throttle from 'lodash/throttle';
import { loadState, saveState } from './localStorage';
import { configureStore } from '@reduxjs/toolkit';
import type { RootState } from './reducers';
import rootReducer from './reducers';
import { isProdEnv } from './environment';
import { canvasLayersInitialState } from './reducers/canvasLayersReducer';
import { metadataMappingPersistedStateHandler } from './components/pages/canvas-pages/united-canvas/process-mining/metadataMappingPersistence';
import {
  agentsHomePersistedStateLoadHandler,
  agentsHomePersistedStateSaveHandler,
} from './components/aiAgentCenter/agentsHomePersistence';

const rawPersistedState = loadState();
const persistedState = rawPersistedState?.agentsHome
  ? {
      ...rawPersistedState,
      agentsHome: agentsHomePersistedStateLoadHandler(rawPersistedState.agentsHome),
    }
  : rawPersistedState;

// Vitest uses import.meta.env.MODE === 'test' (process is not available in the browser bundle).
// RTK then keeps immutable/serializable invariant middleware on; large preloadedState can exceed
// its warn threshold and spam stderr during tests without signaling a real bug.
const vitestMiddlewareOptions =
  import.meta.env.MODE === 'test'
    ? { immutableCheck: false as const, serializableCheck: false as const }
    : {};

export const storeBuilder = (preloadedState?: RootState) => {
  const store = configureStore({
    reducer: rootReducer,
    preloadedState: preloadedState || persistedState,
    devTools: !isProdEnv,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware(vitestMiddlewareOptions),
  });

  store.subscribe(
    throttle(() => {
      // If you want to persist state variables in local storage
      // here is the place:
      const persistedStateOnLocalStorage = {
        debugInfo: store.getState().debugInfo,
        canvasLayersV2: { ...canvasLayersInitialState, ...store.getState().canvasLayersV2 },
        templateFieldsMapping: store.getState().templateFieldsMapping,
        metadataMapping: metadataMappingPersistedStateHandler(store.getState().metadataMapping),
        scopedEnv: store.getState().scopedEnv,
        agentsHome: agentsHomePersistedStateSaveHandler(store.getState()),
      };
      saveState(persistedStateOnLocalStorage);
    }, 1000),
  );

  return { store };
};
